// Multipart upload to S3 via backend presign endpoints.
// Used to upload large videos without hitting API Gateway/Lambda body limits.

const DEFAULT_PART_SIZE = 8 * 1024 * 1024; // 8MB (min for S3 multipart is 5MB except last)

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
const jitter = (ms) => {
  const n = Number(ms) || 0;
  if (n <= 0) return 0;
  const spread = n * 0.2;
  return Math.max(0, Math.round(n - spread + Math.random() * spread * 2));
};
const isRetryableStatus = (status) =>
  [408, 425, 429, 500, 502, 503, 504, 520, 522, 524].includes(Number(status));

function stripQuotes(s) {
  return String(s || "").trim().replace(/^"+|"+$/g, "");
}

/**
 * Upload 1 request (POST /media/upload, /settings/assets, avatar...) bị giới hạn ~4.5MB ở BE (413 FILE_TOO_LARGE).
 * Tệp lớn hơn ngưỡng này phải đi đường multipart (presigned PUT thẳng lên S3).
 */
export const SINGLE_UPLOAD_MAX_BYTES = 4 * 1024 * 1024;

export const TOO_LARGE_SINGLE_UPLOAD_MESSAGE = "Tệp quá lớn (tối đa 4MB). Vui lòng nén hoặc giảm kích thước ảnh rồi thử lại.";

/** Ném lỗi tiếng Việt rõ ràng khi tệp vượt giới hạn upload 1 request (đường không hỗ trợ multipart). */
export function assertSingleUploadSize(file) {
  if (Number(file?.size || 0) > SINGLE_UPLOAD_MAX_BYTES) {
    const err = new Error(TOO_LARGE_SINGLE_UPLOAD_MESSAGE);
    err.code = "FILE_TOO_LARGE";
    err.status = 413;
    throw err;
  }
}

const MIME_EXT = {
  "image/jpeg": "jpg",
  "image/png": "png",
  "image/webp": "webp",
  "image/gif": "gif",
  "image/svg+xml": "svg",
  "image/avif": "avif",
  "video/mp4": "mp4",
  "video/webm": "webm",
  "video/quicktime": "mov",
  "application/pdf": "pdf",
};

const EXT_MIME = {
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  png: "image/png",
  webp: "image/webp",
  gif: "image/gif",
  avif: "image/avif",
  ico: "image/x-icon",
  svg: "image/svg+xml",
  mp4: "video/mp4",
  m4v: "video/mp4",
  webm: "video/webm",
  mov: "video/quicktime",
  qt: "video/quicktime",
  pdf: "application/pdf",
};

/**
 * Content-Type cho multipart create: BE chỉ nhận ảnh/video/pdf (415 UNSUPPORTED_TYPE) →
 * khi trình duyệt không cho file.type (vd .mov trên một số máy) thì đoán theo phần mở rộng.
 */
export function guessUploadContentType(file, fallback = "") {
  const given = String(fallback || file?.type || "").trim().toLowerCase();
  if (given && given !== "application/octet-stream") return given;
  const m = String(file?.name || "").match(/\.([A-Za-z0-9]{1,8})$/);
  return (m && EXT_MIME[m[1].toLowerCase()]) || given || "application/octet-stream";
}

function slugifyBase(value, fallback = "file") {
  const out = String(value || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/đ/g, "d")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60)
    .replace(/-+$/g, "");
  return out || fallback;
}

/** Hậu tố ngắn gần như duy nhất: thời gian (base36) + ngẫu nhiên. */
export function uniqueSuffix() {
  return `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
}

/**
 * Tên tệp duy nhất cho mỗi lần upload: "<tên-slug>-<hậu tố>.<ext>"
 * (tránh ghi đè khi nhiều tệp trùng tên: image.png từ clipboard, IMG_0001.jpg...).
 */
export function uniqueFileName(fileOrName, mimeType = "") {
  const rawName = typeof fileOrName === "string" ? fileOrName : String(fileOrName?.name || "file");
  const mime = mimeType || (typeof fileOrName === "object" ? String(fileOrName?.type || "") : "");
  const m = rawName.match(/\.([A-Za-z0-9]{1,8})$/);
  const ext = (m ? m[1].toLowerCase() : "") || MIME_EXT[mime] || "bin";
  const base = slugifyBase(rawName.replace(/\.[^.]+$/, ""), "file");
  return `${base}-${uniqueSuffix()}.${ext}`;
}

/** Key S3 duy nhất trong prefix (prefix có hoặc không có "/" cuối). */
export function buildUniqueUploadKey(prefix, fileOrName, mimeType = "") {
  const p = String(prefix || "").replace(/^\/+/, "");
  const dir = p && !p.endsWith("/") ? `${p}/` : p;
  return `${dir}${uniqueFileName(fileOrName, mimeType)}`;
}

function dirOfKey(key) {
  const s = String(key || "");
  const i = s.lastIndexOf("/");
  return i >= 0 ? s.slice(0, i + 1) : "";
}

/**
 * Upload multipart lên S3. Nếu key đã tồn tại (409 KEY_EXISTS) sẽ tự tạo key mới (cùng thư mục) và thử lại 1 lần.
 * Luôn dùng `key`/`url` trong kết quả trả về — có thể khác key truyền vào.
 */
export async function multipartUploadToS3({
  apiBase,
  token,
  key,
  file,
  contentType,
  cacheControl = "public, max-age=31536000, immutable",
  partSize = DEFAULT_PART_SIZE,
  onProgress,
}) {
  if (!apiBase) throw new Error("Missing apiBase");
  if (!key) throw new Error("Missing key");
  if (!(file instanceof File)) throw new Error("Missing file");

  const rawKey = String(key || "").trim();
  const protectedRoots = new Set(["site", "users", "locations"]);
  const rootOnly = rawKey.replace(/^\/+/, "").replace(/\\/g, "/").replace(/\/+$/, "");
  if (protectedRoots.has(rootOnly)) {
    throw new Error(`Key upload không hợp lệ: "${rawKey}"`);
  }

  const headersAuth = token ? { Authorization: `Bearer ${token}` } : {};
  const postJsonRetries = 6;
  const postJson = async (path, body) => {
    let lastErr = null;
    for (let attempt = 0; attempt <= postJsonRetries; attempt++) {
      try {
        const res = await fetch(`${apiBase}${path}`, {
          method: "POST",
          headers: { "Content-Type": "application/json", ...headersAuth },
          body: JSON.stringify(body),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          const msg = data?.message || "Multipart upload failed";
          const err = new Error(`${msg} (key="${body?.key || rawKey}", file="${file?.name || ""}")`);
          err.status = res.status;
          err.code = data?.code;
          const canRetry = attempt < postJsonRetries && isRetryableStatus(res.status);
          if (canRetry) {
            lastErr = err;
            await sleep(jitter(Math.min(12000, 700 * Math.pow(2, attempt))));
            continue;
          }
          throw err;
        }
        return data?.data || data;
      } catch (e) {
        const isNetwork = e instanceof TypeError || /failed to fetch|network/i.test(String(e?.message || ""));
        const canRetry = attempt < postJsonRetries && (isNetwork || isRetryableStatus(e?.status));
        if (canRetry) {
          lastErr = e;
          await sleep(jitter(Math.min(12000, 700 * Math.pow(2, attempt))));
          continue;
        }
        throw e;
      }
    }
    throw lastErr || new Error("Multipart request failed");
  };

  const total = file.size || 0;
  const size = Math.max(5 * 1024 * 1024, Number(partSize) || DEFAULT_PART_SIZE);
  const partCount = Math.max(1, Math.ceil(total / size));

  // 1) Create (key trùng → BE trả 409 KEY_EXISTS: tạo key mới và thử lại 1 lần)
  const createBody = (k) => ({
    key: k,
    contentType: guessUploadContentType(file, contentType),
    cacheControl,
  });
  let created;
  try {
    created = await postJson("/media/multipart/create", createBody(key));
  } catch (e) {
    if (e?.code !== "KEY_EXISTS" && Number(e?.status) !== 409) throw e;
    key = buildUniqueUploadKey(dirOfKey(rawKey), file);
    created = await postJson("/media/multipart/create", createBody(key));
  }
  if (created?.key) key = String(created.key);

  const uploadId = created?.uploadId;
  if (!uploadId) throw new Error("Missing uploadId");

  let uploadedBytes = 0;
  const parts = [];

  try {
    for (let partNumber = 1; partNumber <= partCount; partNumber++) {
      const start = (partNumber - 1) * size;
      const end = Math.min(total, start + size);
      const blob = file.slice(start, end);

      // 2) Sign part
      const signed = await postJson("/media/multipart/sign-part", {
        key,
        uploadId,
        partNumber,
      });
      const url = signed?.url;
      if (!url) throw new Error("Missing presigned url");

      // 3) PUT part to S3
      const putRes = await fetch(url, {
        method: "PUT",
        body: blob,
      });
      if (!putRes.ok) throw new Error(`Upload part ${partNumber} failed`);

      const etag = stripQuotes(putRes.headers.get("etag"));
      if (!etag) throw new Error(`Missing ETag for part ${partNumber}`);
      parts.push({ ETag: etag, PartNumber: partNumber });

      uploadedBytes += blob.size;
      if (typeof onProgress === "function") {
        try {
          onProgress({
            uploadedBytes,
            totalBytes: total,
            partNumber,
            partCount,
          });
        } catch {
          // ignore
        }
      }
    }

    // 4) Complete
    const completed = await postJson("/media/multipart/complete", {
      key,
      uploadId,
      parts,
    });

    return completed; // { key, url }
  } catch (e) {
    // best-effort abort
    try {
      await postJson("/media/multipart/abort", { key, uploadId });
    } catch {
      // ignore
    }
    throw e;
  }
}

